import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider.js";
import {
  ApiError,
  uploadInsuranceCert,
  useSubmitApplication,
  type ApplicationBody,
} from "../portal/api.js";
import {
  cityLabelsForTownKeys,
  townKeysForZips,
  zipsForTownKeys,
} from "../serviceArea/qcServiceArea.js";
import { emptyDraft, useGenerateDraft, type ProfileDraft } from "./api.js";
import { capabilityOptionsFor } from "./capabilities.js";
import { AccountStep, ConfirmStep, type AccountInput } from "./AccountStep.js";
import { DoneStep } from "./DoneStep.js";
import { GeneratingStep } from "./GeneratingStep.js";
import { PreferencesStep, type LeadPrefs } from "./PreferencesStep.js";
import { ReviewStep } from "./ReviewStep.js";
import { ProgressHeader } from "./ui.js";
import { VerifyStep, type VerifyInput } from "./VerifyStep.js";
import { WebsiteStep } from "./WebsiteStep.js";

/**
 * AI-assisted contractor onboarding wizard (route `/start`). Single route, one
 * in-memory draft, sessionStorage-persisted so a refresh doesn't lose the
 * magical result. Flow: website -> generating -> review -> account -> confirm
 * -> verify -> preferences -> done. After email confirmation the pro is signed
 * in (Cognito), and the final step submits the whole thing as a PENDING_REVIEW
 * application via the existing /portal/application path.
 */

type Step =
  | "website"
  | "generating"
  | "review"
  | "account"
  | "confirm"
  | "verify"
  | "preferences"
  | "done";

interface WizardState {
  step: Step;
  websiteUrl: string;
  draft: ProfileDraft | null;
  account: { contactName: string; email: string; mobile: string };
  verify: VerifyInput;
  prefs: LeadPrefs;
}

const STORAGE_KEY = "qcpro.onboarding";

const PHASE_INDEX: Record<Step, number> = {
  website: 0,
  generating: 0,
  review: 0,
  account: 1,
  confirm: 1,
  verify: 2,
  preferences: 3,
  done: 3,
};

const BACK_TO: Partial<Record<Step, Step>> = {
  review: "website",
  account: "review",
  confirm: "account",
  verify: "review",
  preferences: "verify",
};

function defaultPrefs(): LeadPrefs {
  return {
    selectedTowns: [],
    leadDelivery: { email: true, sms: false },
    businessRules: {
      minJobSize: "",
      freeEstimates: true,
      emergencyCalls: false,
      residential: true,
      commercial: false,
    },
  };
}

function initialState(): WizardState {
  return {
    step: "website",
    websiteUrl: "",
    draft: null,
    account: { contactName: "", email: "", mobile: "" },
    verify: { licenseNumber: "", insuranceInfo: "" },
    prefs: defaultPrefs(),
  };
}

function loadState(): WizardState {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return initialState();
    const saved = JSON.parse(raw) as Partial<WizardState>;
    // Never resume mid-generation; treat it as "re-enter the URL".
    if (saved.step === "generating") saved.step = "website";
    return { ...initialState(), ...saved };
  } catch {
    return initialState();
  }
}

export function Onboarding() {
  const navigate = useNavigate();
  const { signUp, confirmSignUp, signIn, resendCode } = useAuth();
  const generate = useGenerateDraft();
  const submitApplication = useSubmitApplication();

  const [state, setState] = useState<WizardState>(loadState);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Password is held only in memory (never persisted) for the post-confirm sign-in.
  const passwordRef = useRef<string>("");

  // Persist everything except the password + transient flags.
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // Storage blocked — the wizard still works in-memory for this session.
    }
  }, [state]);

  const patch = (p: Partial<WizardState>) => setState((s) => ({ ...s, ...p }));
  const setStep = (step: Step) => {
    setError(null);
    setInfo(null);
    patch({ step });
  };

  function seedFromDraft(draft: ProfileDraft): Partial<WizardState> {
    return {
      draft,
      account: {
        contactName: "",
        email: draft.email ?? "",
        mobile: draft.phone ?? "",
      },
      prefs: {
        ...defaultPrefs(),
        selectedTowns: townKeysForZips(draft.serviceAreaZips),
        businessRules: {
          ...defaultPrefs().businessRules,
          emergencyCalls: draft.emergencyService === true,
        },
      },
    };
  }

  async function startGeneration(url: string) {
    setError(null);
    patch({ websiteUrl: url, step: "generating" });
    try {
      const draft = await generate.mutateAsync(url);
      setState((s) => ({ ...s, ...seedFromDraft(draft), step: "review" }));
    } catch {
      // Network/unknown failure — never dead-end; fall through to a blank profile.
      const draft = { ...emptyDraft("manual"), website: url };
      setState((s) => ({ ...s, ...seedFromDraft(draft), step: "review" }));
    }
  }

  function startBlank() {
    const draft = emptyDraft("manual");
    setState((s) => ({ ...s, ...seedFromDraft(draft), step: "review" }));
  }

  async function createAccount(input: AccountInput) {
    setBusy(true);
    setError(null);
    passwordRef.current = input.password;
    patch({
      account: { contactName: input.contactName, email: input.email, mobile: input.mobile },
    });
    try {
      const givenName = input.contactName.split(/\s+/)[0];
      const { needsConfirmation } = await signUp({
        email: input.email,
        password: input.password,
        ...(givenName ? { givenName } : {}),
      });
      if (needsConfirmation) {
        setStep("confirm");
      } else {
        await signIn({ email: input.email, password: input.password });
        setStep("verify");
      }
    } catch (err) {
      setError(authMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function confirmAccount(code: string) {
    setBusy(true);
    setError(null);
    try {
      await confirmSignUp({ email: state.account.email, code });
      const pw = passwordRef.current;
      if (pw) {
        await signIn({ email: state.account.email, password: pw });
        passwordRef.current = ""; // don't keep the plaintext password around
        setStep("verify");
      } else {
        // Password lost (e.g. refresh) — finish by signing in manually.
        navigate("/sign-in", { state: { from: "/start" } });
      }
    } catch (err) {
      setError(authMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    setError(null);
    setInfo(null);
    try {
      await resendCode({ email: state.account.email });
      setInfo("Sent a new code. Check your inbox.");
    } catch (err) {
      setError(authMessage(err));
    }
  }

  async function finish() {
    const { draft, account, prefs, verify } = state;
    if (!draft) return;
    const validationError = validateForSubmit(state);
    if (validationError) {
      setError(validationError);
      return;
    }
    setBusy(true);
    setError(null);

    const website = normalizeWebsite(draft.website);
    const hasSocial = Object.keys(draft.socialLinks).length > 0;
    const rules = cleanRules(prefs.businessRules);
    // Keep only capabilities that belong to the chosen categories.
    const validCaps = new Set(capabilityOptionsFor(draft.serviceCategories).map((o) => o.value));
    const capabilities = draft.capabilities.filter((c) => validCaps.has(c));

    const body: ApplicationBody = {
      businessName: (draft.businessName ?? "").trim(),
      contactName: account.contactName.trim(),
      email: account.email.trim(),
      phone: account.mobile.trim(),
      ...(website ? { website } : {}),
      serviceCategories: draft.serviceCategories,
      serviceArea: {
        postalCodes: zipsForTownKeys(prefs.selectedTowns),
        cities: cityLabelsForTownKeys(prefs.selectedTowns),
      },
      yearsExperience: draft.yearsInBusiness ?? 0,
      ...(draft.description ? { about: draft.description } : {}),
      ...(draft.servicesOffered.length ? { services: draft.servicesOffered } : {}),
      ...(capabilities.length ? { capabilities } : {}),
      ...(draft.logoUrl ? { logoUrl: draft.logoUrl } : {}),
      ...(draft.photos.length ? { photos: draft.photos } : {}),
      ...(hasSocial ? { socialLinks: draft.socialLinks } : {}),
      leadDelivery: prefs.leadDelivery,
      ...(rules ? { businessRules: rules } : {}),
      ...(verify.licenseNumber ? { licenseNumber: verify.licenseNumber } : {}),
      ...(verify.insuranceInfo ? { insuranceInfo: verify.insuranceInfo } : {}),
      ...(verify.insuranceCertKey ? { insuranceCertKey: verify.insuranceCertKey } : {}),
      ...(verify.insuranceCertName ? { insuranceCertName: verify.insuranceCertName } : {}),
    };

    try {
      await submitApplication.mutateAsync(body);
      try {
        sessionStorage.removeItem(STORAGE_KEY);
      } catch {
        /* ignore */
      }
      setStep("done");
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError(
          "A pro profile already exists for that business email. If it's yours, sign in with that email and it links automatically.",
        );
      } else if (err instanceof ApiError && err.status === 400) {
        setError("Please double-check your details — some fields look off.");
      } else {
        setError(err instanceof Error ? err.message : "Could not submit. Please try again.");
      }
    } finally {
      setBusy(false);
    }
  }

  const back = BACK_TO[state.step];
  const showChrome =
    state.step !== "website" && state.step !== "generating" && state.step !== "done";

  return (
    <div className="min-h-dvh bg-field-gray">
      {showChrome && (
        <div className="mx-auto max-w-md px-5 pt-6">
          <div className="mb-4 flex h-6 items-center">
            {back && (
              <button
                type="button"
                onClick={() => setStep(back)}
                className="text-sm font-medium text-brand-700"
              >
                &larr; Back
              </button>
            )}
          </div>
          <ProgressHeader phaseIndex={PHASE_INDEX[state.step]} />
        </div>
      )}

      {state.step === "website" && (
        <WebsiteStep onSubmit={startGeneration} onNoWebsite={startBlank} error={error} />
      )}
      {state.step === "generating" && <GeneratingStep websiteUrl={state.websiteUrl} />}
      {state.step === "review" && state.draft && (
        <ReviewStep
          draft={state.draft}
          onChange={(p) => patch({ draft: { ...state.draft!, ...p } })}
          onContinue={() => setStep("account")}
        />
      )}
      {state.step === "account" && (
        <AccountStep
          initialEmail={state.account.email}
          initialMobile={state.account.mobile}
          onSubmit={createAccount}
          submitting={busy}
          error={error}
        />
      )}
      {state.step === "confirm" && (
        <ConfirmStep
          email={state.account.email}
          onConfirm={confirmAccount}
          onResend={resend}
          submitting={busy}
          error={error}
          info={info}
        />
      )}
      {state.step === "verify" && (
        <VerifyStep
          initial={state.verify}
          onUpload={uploadInsuranceCert}
          onContinue={(values) => {
            patch({ verify: values });
            setStep("preferences");
          }}
          onSkip={() => setStep("preferences")}
        />
      )}
      {state.step === "preferences" && (
        <PreferencesStep
          prefs={state.prefs}
          onChange={(p) => patch({ prefs: { ...state.prefs, ...p } })}
          onFinish={finish}
          submitting={busy}
          error={error}
        />
      )}
      {state.step === "done" && (
        <DoneStep businessName={(state.draft?.businessName ?? "").trim()} />
      )}
    </div>
  );
}

function validateForSubmit(state: WizardState): string | null {
  const { draft, account, prefs } = state;
  if (!draft) return "Something went wrong — please start over.";
  if (!(draft.businessName ?? "").trim()) return "Add your business name on the profile step.";
  if (draft.serviceCategories.length === 0)
    return "Pick at least one service category on the profile step.";
  if (!account.contactName.trim()) return "Add your name.";
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(account.email.trim())) return "Add a valid email.";
  if (account.mobile.replace(/\D/g, "").length < 7) return "Add a valid mobile number.";
  if (prefs.selectedTowns.length === 0) return "Pick the towns you serve on the map.";
  return null;
}

/** Coerce free user input into a valid http(s) URL, or undefined to omit it. */
function normalizeWebsite(raw: string): string | undefined {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const url = new URL(withScheme);
    // http/https only — never let a javascript:/data: value through.
    if (url.protocol !== "http:" && url.protocol !== "https:") return undefined;
    return url.toString();
  } catch {
    return undefined;
  }
}

/** Drop an empty minJobSize so the schema's optional/url checks stay happy. */
function cleanRules(
  rules: LeadPrefs["businessRules"],
): ApplicationBody["businessRules"] | undefined {
  const minJobSize = rules.minJobSize.trim();
  return {
    ...(minJobSize ? { minJobSize } : {}),
    freeEstimates: rules.freeEstimates,
    emergencyCalls: rules.emergencyCalls,
    residential: rules.residential,
    commercial: rules.commercial,
  };
}

function authMessage(err: unknown): string {
  if (err instanceof Error) {
    if (err.name === "UsernameExistsException") {
      return "You already have an account with that email. Sign in instead — your profile is saved here.";
    }
    return err.message;
  }
  return "Something went wrong. Please try again.";
}
